/**
 * Frontend Constants
 * 
 * Client-side application constants.
 */

// ============================================
// Cookie Configuration
// ============================================

/** Session cookie name (must match backend) */
export const SESSION_COOKIE_NAME = 'kirjuri_session';

// ============================================
// API Configuration
// ============================================

/** Default API request timeout in milliseconds */
export const API_REQUEST_TIMEOUT_MS = 30000;

/** Maximum number of retry attempts for failed requests */
export const API_MAX_RETRY_ATTEMPTS = 3;

/** Base delay for exponential backoff in milliseconds */
export const API_RETRY_BASE_DELAY_MS = 1000;

/** Maximum delay for exponential backoff in milliseconds */
export const API_RETRY_MAX_DELAY_MS = 10000;

// ============================================
// Pagination
// ============================================

/** Default number of items per page */
export const DEFAULT_PAGE_SIZE = 25;

/** Available page size options */
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// ============================================
// UI Constants
// ============================================

/** Debounce delay for search inputs in milliseconds */
export const SEARCH_DEBOUNCE_MS = 300;

/** Toast/notification duration in milliseconds */
export const TOAST_DURATION_MS = 5000;

/** Number of items to show in autocomplete dropdown */
export const AUTOCOMPLETE_MAX_ITEMS = 10;

// ============================================
// Date/Time Formats
// ============================================

/** Default date format (Finnish locale) */
export const DATE_FORMAT = 'dd.MM.yyyy';

/** Default date-time format */
export const DATETIME_FORMAT = 'dd.MM.yyyy HH:mm';

/** Month format for selectors */
export const MONTH_FORMAT = 'MMMM yyyy';

// ============================================
// File Upload
// ============================================

/** Maximum file upload size in bytes (10MB) */
export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

/** Allowed file types for upload */
export const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/json',
  'text/csv',
];

/** Allowed file extensions */
export const ALLOWED_FILE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.pdf', '.json', '.csv'];

// ============================================
// Validation
// ============================================

/** Minimum password length */
export const MIN_PASSWORD_LENGTH = 8;

/** Maximum length for text inputs */
export const MAX_TEXT_INPUT_LENGTH = 255;

/** Maximum length for description fields */
export const MAX_DESCRIPTION_LENGTH = 2000;

// ============================================
// VAT Rates (must match backend)
// ============================================

export const VAT_RATES = [0, 10, 14, 25.5] as const;
export type VatRate = typeof VAT_RATES[number];

// ============================================
// Finnish Account Codes (subset for UI)
// ============================================

/** Common account codes for quick selection in UI */
export const COMMON_ACCOUNT_CODES = [
  { code: '3000', name: 'Jäsenmaksutulot' },
  { code: '3010', name: 'Osallistumismaksut' },
  { code: '3020', name: 'Kurssimaksut' },
  { code: '3030', name: 'Kilpailumaksut' },
  { code: '3050', name: 'Muut toimintatulot' },
  { code: '4000', name: 'Palkat ja palkkiot' },
  { code: '4100', name: 'Henkilösivukulut' },
  { code: '4200', name: 'Matkakulut' },
  { code: '4300', name: 'Vuokrakulut' },
  { code: '4400', name: 'Koulutuskulut' },
  { code: '4500', name: 'Tapahtuma- ja kilpailukulut' },
  { code: '4600', name: 'Välinehankinnat' },
  { code: '4700', name: 'Markkinointi ja viestintä' },
  { code: '4800', name: 'Toimistotarvikkeet' },
  { code: '4900', name: 'Muut toimintakulut' },
  { code: '5000', name: 'Myyntitulot (varainhankinta)' },
  { code: '5030', name: 'Lahjoitukset' },
  { code: '6000', name: 'Kunta-avustukset' },
  { code: '6010', name: 'Valtion avustukset' },
  { code: '7000', name: 'Korkotulot' },
  { code: '7110', name: 'Pankkikulut' },
];
